import {
  FieldList,
  IFormGroupRules,
  IFormRules,
} from '@components/form/services';
import {
  CELL_RENDERER,
  DROPDOWN_MENU_POSITION,
  ENTITY_TYPES,
  ITransferFormFieldPath,
  ListColumn,
  TOOLBAR_BUTTON_POSITION,
  TRANSFER_TYPE,
  TRANSFER_TYPE_ENUM_OPTIONS,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  SORT_ORDERS,
  DEFAULT_GRID_HEIGHT,
  MEASURE_TRANSACTION,
  IMeasure,
} from '@shared/types';
import {
  generateUnitTextV2,
  getObservableValue,
  getQuickFilterConfigForScanning,
  getScanningHighlightRowClass,
} from '@shared/utils/common-utils';
import { get, includes, isEmpty } from 'lodash';
import {
  DEFAULT_ITEM_LIST_TABLE_HEIGHT,
  MAX_NUMBER_FOR_TRANSFER_QUANTITY,
  MIN_NUMBER_FOR_TRANSFER_QUANTITY,
  WIDGET_FAMILY,
} from '@config/global-constants';
import {
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { rx } from '@shared/rx';
import {
  DISPLAY_EVENT_TYPES,
  TRANSFER_FINAL_STATUS,
  TRANSFER_NOT_FINAL_STATUS,
} from '@shared/services/transfer/transfer.definition';
import {
  COMMON_GENERAL_MESSAGE_CLASS,
  COMMON_ICONS_COLUMN,
  COMMON_WARN_ICON_COLUMN,
  COMMON_WARN_MESSAGE_CLASS,
} from '@app/shared/constants/common-warn-grid-setting';
import { TransferEditContainerComponent } from '@containers/transfer/containers';
import { calculateUnitCost, getParQtyColumn } from './transfer.util';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { TransferFormService } from '@shared/services/transfer/transfer.form.service';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { getListGridRowCellClassForTransfer } from '@shared/helpers';
import {
  DecimalNumber,
  getGroupStrCaseInsensitiveComparator,
  stringCaseInsensitiveComparator,
} from '@app/shared/common';
import { generateDecimalRegexp } from '@app/shared/constants';
import {
  BTN_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
  BTN_REPLENISH_QOH,
  BTN_REPLENISH_ZERO_QOH,
  GROUP_BTN_REPLENISH_BASE,
} from '../transfer-replenish/transfer-replenish.constants';
import {
  CLEAR_QTY,
  CLEAR_QTY_RECEIVED,
  CLEAR_QTY_REQUESTED,
} from './transfer-constants';
import { transferGeneralFields } from '@shared/services/transfer/transfer-column.definition';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { I18n } from '@app/i18n/strings-keys';

export const createFormRules = (
  formService: TransferFormService,
  editor: TransferEditContainerComponent,
): IFormRules => {
  const formFieldPath: ITransferFormFieldPath = formService.formFieldPath;
  const { fields: totalFields } =
    formService.totalFromService.getTotalsFormRules(
      formService.formFieldPath,
      formService.isShownSubmittedColumns$,
    );

  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
    },
    item_list_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };

  const getIconsInfoColumns = [
    {
      ...COMMON_WARN_ICON_COLUMN,
      hide: true,
    },
    {
      ...COMMON_ICONS_COLUMN,
      cellRendererParams: formService.getIconsInfoRendererParams.bind(
        formService,
      )(formService.formFieldPath.siteIdTo),
    },
  ];

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: ['general_section', 'item_list_section'],
    },
    {
      id: 'general_section',
      fields: [
        'type',
        'open_draft_message',
        'transfer_group_transfer_message',
        'transfer_type',
        'source',
        'event_id',
        'site_id',
        'site_id_to',
        'store_number',
        'store_number_to',
        'transaction_date',
        'virtual.transaction_date',
        'status',
      ],
    },
    {
      id: 'item_list_section',
      fields: [
        'remove_message',
        'virtual.duplicate_unavailable_items',
        'modified_message',
        'replenish_message',
        'threshold_message',
        'inventory_items',
        'virtual.totals',
      ],
    },
  ];

  const defaultIconsInfoForMessage = {
    iconConfig: {
      icon: 'icon-warning',
      class: 'xvi-grid-warn-icon',
    },
  };

  const quickFilterOptions = getQuickFilterConfigForScanning(
    formService.isCategoryListView(),
  );

  return {
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        totals: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              item_count: { type: 'number' },
              total_cost: { type: 'number' },
            },
          },
        },
        inventory_items: {
          type: 'array',
        },
        transaction_date: {
          type: ['string', 'object'],
        },
        /**
         * for cache autoPopulate status and data
         */
        autoPopulate: { type: 'object' },
        duplicate_unavailable_items: {
          type: 'array',
        },
      },
    },
    fields: {
      ...sections,
      ...transferGeneralFields({ i18nService: editor.i18nService }),
      type: {
        show: false,
        defaultValue: ENTITY_TYPES.TRANSFER,
      },
      transfer_group_transfer_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        title: I18n.InfoMessages.TransferIsAPartOfGroup,
        show: (value): boolean =>
          !isEmpty(get(value, 'transfer_group_entity_id')),
        widgetData: {
          itemClass: COMMON_GENERAL_MESSAGE_CLASS,
        },
      },
      transfer_type: {
        title: I18n.Common.Type,
        widget: WIDGET_COMPONENT.select,
        optionText: TRANSFER_TYPE_ENUM_OPTIONS,
        defaultValue: TRANSFER_TYPE.PRE_EVENT_TRANSFER,
        disabled: true,
      },
      event_id: {
        title: I18n.EntityNames.Event,
        widget: WIDGET_COMPONENT.entity_select,
        required: true,
        disabledObs: formService.isDisableTopSection$,
        widgetData: {
          entity: ENTITY_TYPES.EVENTS,
          dataSource: formService.eventIdDataSource,
          lazy: true,
          hideEmptyOption: true,
          enableClientFiltering: true,
          artificialInputCheck: true,
        },
        show: ({ transfer_type }): boolean =>
          includes(DISPLAY_EVENT_TYPES, transfer_type),
        omit: ({ transfer_type }): boolean =>
          !includes(DISPLAY_EVENT_TYPES, transfer_type),
        defaultValue: null,
        validators: [formService.validationService.requiredValidator],
        asyncValidators: [formService.getEventValidator.bind(formService)()],
      },
      site_id: {
        title: I18n.Common.From,
        widget: WIDGET_COMPONENT.entity_select,
        required: true,
        disabledObs: formService.isDisableTopSection$,
        widgetData: {
          entity: ENTITY_TYPES.SITE,
          dataSource: formService.siteIdDataSource,
          lazy: true,
          enableClientFiltering: true,
          artificialInputCheck: true,
        },
        validators: [
          formService.validationService.requiredValidator,
          formService.getSameValueValidator('site_id_to'),
        ],
      },
      site_id_to: {
        title: I18n.Common.To,
        widget: WIDGET_COMPONENT.entity_select,
        required: true,
        disabledObs: formService.siteIdToDisabled$,
        widgetData: {
          entity: ENTITY_TYPES.SITE,
          dataSource: formService.siteIdToDataSource,
          lazy: true,
          enableClientFiltering: true,
          artificialInputCheck: true,
        },
        validators: [
          formService.validationService.requiredValidator,
          formService.getSameValueValidator('site_id'),
        ],
      },
      store_number: {
        required: false,
        show: false,
      },
      store_number_to: {
        required: false,
        show: false,
      },
      site_name_to: {
        required: false,
        show: false,
        maxLength: 388, // site name(256) + store number(128) + 4([  ])
      },
      site_name: {
        required: false,
        show: false,
        maxLength: 388,
      },
      transaction_date: {
        title: I18n.Common.Date,
        widget: WIDGET_COMPONENT.xux_datepicker,
        required: true,
        widgetData: {
          minimumObs: formService.transactionDateLimit.minimumObs,
          maximumObs: formService.transactionDateLimit.maximumObs,
        },
        validators: [
          (control: AbstractControl): ValidationErrors | null => {
            // Get the current transfer type from the form
            const currentTransferType =
              editor.form?.get('transfer_type')?.value;
            const currentTransferStatus = editor.form?.get('status')?.value;
            const isPreEventTransfer =
              currentTransferType === TRANSFER_TYPE.PRE_EVENT_TRANSFER;

            return formService.getTransactionDateRangeValidator(
              getObservableValue(formService.transactionDateLimit.maximumObs),
              getObservableValue(formService.transactionDateLimit.minimumObs),
              isPreEventTransfer,
              includes(TRANSFER_FINAL_STATUS, currentTransferStatus),
            )(control);
          },
        ],
        disabled: (): boolean => formService.getTransferDateDisable(),
      },
      'virtual.transaction_date': {
        show: false,
      },
      status: {
        title: I18n.Common.Status,
        disabled: true,
        widget: WIDGET_COMPONENT.select,
        required: false,
        selectOptions: formService.transferStatusOptions$,
        defaultValue: TRANSFER_STATUS_VALUE.New,
      },
      'virtual.duplicate_unavailable_items': {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        title: I18n.InfoMessages.SomeItemsCannotBeDuplicated,
        show: ({ virtual: { duplicate_unavailable_items } }) =>
          editor.isDuplicateMode && !isEmpty(duplicate_unavailable_items),
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          otherTips: formService.i18nService.instant(
            I18n.Common.PleaseViewDetails,
          ),
          clickFn: () =>
            formService.transferService.showCannotDuplicatedDialogHandler(
              get(
                formService.form.value,
                ['virtual', 'duplicate_unavailable_items'],
                [],
              ),
            ),
        },
      },
      remove_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.removeInfoTitle$,
        hiddenObs: formService.hiddenRemoveInfo$,
        show: true,
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          ...defaultIconsInfoForMessage,
        },
      },
      modified_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.modifiedInfoTitle$,
        hiddenObs: formService.hiddenModifiedInfo$,
        show: true,
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          ...defaultIconsInfoForMessage,
        },
      },
      replenish_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.replenishMessageTitle$,
        hiddenObs: formService.replenishMessageHidden$,
        show: true,
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          ...defaultIconsInfoForMessage,
        },
      },
      threshold_message: {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        titleObs: formService.thresholdInfoTitle$,
        hiddenObs: formService.hiddenThresholdInfo$,
        show: true,
        widgetData: {
          itemClass: COMMON_WARN_MESSAGE_CLASS,
          iconConfig: {
            icon: 'icon-warning',
            class: 'xvi-grid-dark-warning-icon',
          },
        },
      },
      inventory_items: {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        title: I18n.Forms.GridForm.ItemList,
        class:
          'no-padding one-column-tree-grid transfer-items-grid navigate-grid',
        minItems: 0,
        widgetData: {
          gridHeight: DEFAULT_ITEM_LIST_TABLE_HEIGHT,
          gridMinHeight: DEFAULT_GRID_HEIGHT,
          showLoader: formService.isInventoryItemVenuesGridBusy$,
          useVisibleItemCountDescription: true,
          showPristineErrors: false,
          filterable: true,
          showSearch: true,
          multiselect: false,
          disableColumnSelector: true,
          orderControls: false,
          treeViewMode: formService.isCategoryListView(),
          defaultExpanded: false,
          ignoreItemValueOptimization: true,
          inlineEditOptions: {
            beforeActionMappers: [formService.getInlineChangeMapper()],
          },
          treeChildrenPropertyName: 'children',
          artificialInputCheckForSearchPanel: true,
          userGridOptions: {
            deltaRowDataMode: false,
            suppressMovableColumns: true,
            suppressHorizontalScroll: false,
            suppressClickEdit: true,
            suppressRowClickSelection: true,
            suppressRowTransform: true,
            defaultColDef: {
              editable: true,
            },
            rowClassRules: {
              ...getListGridRowCellClassForTransfer({
                isDuplicate: editor.isDuplicateMode,
              }),
              ...getScanningHighlightRowClass(formService.scanningService),
            },
          },
          buttons: [
            {
              caption: CLEAR_QTY_REQUESTED,
              class: 'btn btn-default mr-10',
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              click: formService.openClearQtyModal.bind(
                formService,
                I18n.Forms.TransferForm.QtyRequested,
                [
                  formFieldPath.initialCount,
                  'initialFactorAndCountValue.initial_count',
                ],
              ),
              disableWithGrid: true,
              disabledObs: formService.clearQtyRequestBtnDisable$.pipe(
                rx.map((value) => !editor.canEdit || value),
              ),
              hidden: () => formService.hideClearQtyRequestedBtn(),
            },
            {
              caption: CLEAR_QTY,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              class: 'btn-default np-non-effect',
              disableWithGrid: true,
              disabledObs: formService.clearQtyGroupBtnDisable$.pipe(
                rx.map((value) => !editor.canEdit || value),
              ),
              hidden: formService.hideClearQtyBtnGroup.bind(formService),
              childItems: [
                {
                  caption: CLEAR_QTY_REQUESTED,
                  click: formService.openClearQtyModal.bind(
                    formService,
                    I18n.Forms.TransferForm.QtyRequested,
                    [
                      formFieldPath.initialCount,
                      'initialFactorAndCountValue.initial_count',
                    ],
                  ),
                },
                {
                  caption: CLEAR_QTY_RECEIVED,
                  click: formService.openClearQtyModal.bind(
                    formService,
                    I18n.Forms.TransferForm.QtyReceived,
                    [formFieldPath.count, 'initialFactorAndCountValue.count'],
                  ),
                },
              ],
            },
            {
              caption: I18n.Common.ViewSetting,
              class: 'btn-primary mr-10 ml-10',
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              click: formService.openViewSettingModal.bind(formService),
              disableWithGrid: false,
              hidden:
                formService.getDifferentStatusHiddenValue.bind(formService),
            },
            {
              ...GROUP_BTN_REPLENISH_BASE,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              class: 'btn-primary np-non-effect',
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              disabledObs: formService.replenishButtonDisabled$.pipe(
                rx.map((value) => !editor.canEdit || value),
              ),
              hidden: () => {
                return formService.getDifferentStatusHiddenValue.bind(
                  formService,
                )();
              },
              childItems: [
                {
                  ...BTN_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                  hidden: () => {
                    return (
                      !formService.isPreEventTypeTransfer() ||
                      formService.getDifferentStatusHiddenValue.bind(
                        formService,
                      )()
                    );
                  },
                  click: formService.onClickReplenishHandler.bind(
                    formService,
                    BTN_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                  ),
                },
                {
                  ...BTN_REPLENISH_QOH,
                  click: formService.onClickReplenishHandler.bind(
                    formService,
                    BTN_REPLENISH_QOH,
                  ),
                },
                {
                  ...BTN_REPLENISH_ZERO_QOH,
                  click: formService.onClickReplenishHandler.bind(
                    formService,
                    BTN_REPLENISH_ZERO_QOH,
                  ),
                },
              ],
            },
          ],
          columns: [
            ...getIconsInfoColumns,
            {
              path: 'reporting_category_major.name',
              filterParams: {
                quickFilter: formService.isCategoryListView(),
              },
            },
            {
              path: 'reporting_category_minor.name',
              filterParams: {
                quickFilter: formService.isCategoryListView(),
              },
            },
            {
              path: 'is_chargeable',
              title: '',
              defaultView: true,
              sortable: !formService.isCategoryListView(),
              sort: SORT_ORDERS.DESC,
              sortingOrder: [SORT_ORDERS.DESC, SORT_ORDERS.ASC],
              headerComponentParams: {
                hintOptions: {
                  description: editor.i18nService.translate(
                    I18n.InfoMessages.$IndicatesChargeableItemsBrackets,
                  ),
                  placement: 'top',
                },
              },
              valueGetter: (params: ValueGetterParams): string => {
                if (!params.data.isLeaf) return null;

                return params.data[formService.formFieldPath.isChargeable]
                  ? '$'
                  : '';
              },
              minWidth: 60,
              maxWidth: 60,
            },
            {
              path: 'name',
              title: I18n.Forms.GridForm.ItemName,
              cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
              cellRendererParams: {
                // turn off the row count
                suppressCount: true,
              },
              defaultView: true,
              sortable: true,
              comparator: formService.isCategoryListView()
                ? getGroupStrCaseInsensitiveComparator()
                : stringCaseInsensitiveComparator,
              showRowGroup: true,
              filterParams: {
                quickFilter: true,
              },
              minWidth: 220,
              ...quickFilterOptions,
            },
            {
              path: 'gl_account.name',
              title: I18n.Forms.GridForm.GLAccount,
              defaultView: true,
              filterParams: {
                quickFilter: true,
              },
              minWidth: 120,
            },
            {
              path: 'qoh_from',
              title: I18n.Forms.TransferForm.QOHFrom,
              defaultView: true,
              valueFormatter: formService.getQohValueGetter('qohFrom'),
              headerComponentParams: {
                hintOptions: {
                  description: editor.i18nService.translate(
                    I18n.InfoMessages.TransferQOHTooltips,
                    {
                      qohFromOrTo: editor.i18nService.translate(
                        I18n.Common.from,
                      ),
                    },
                  ),
                  placement: 'top',
                },
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: 'qoh_to',
              title: I18n.Forms.TransferForm.QOHTo,
              defaultView: true,
              valueFormatter: formService.getQohValueGetter('qohTo'),
              headerComponentParams: {
                hintOptions: {
                  description: editor.i18nService.translate(
                    I18n.InfoMessages.TransferQOHTooltips,
                    {
                      qohFromOrTo: editor.i18nService.translate(I18n.Common.to),
                    },
                  ),
                  placement: 'top',
                },
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: formService.formFieldPath.measureEntityId,
              title: I18n.Forms.TransferForm.TransferUnit,
              defaultView: true,
              editable: true,
              cellRendererSelector: (
                params: ICellRendererParams,
              ): ComponentSelectorResult | null =>
                params.data.isLeaf && !formService.isFinalStatusFun()
                  ? { component: CELL_RENDERER.custom_select_renderer }
                  : null,
              cellRendererParams: (
                params: ICellRendererParams,
              ): ListColumn['cellRendererParams'] =>
                formService.getMeasureEntityIdCellRendererParams(
                  params,
                  editor,
                ),
              valueGetter: (params: ValueGetterParams): string => {
                if (!params.data.isLeaf) return null;

                return formService.isFinalStatusFun()
                  ? generateUnitTextV2(
                      {
                        description: params.data.measure_description,
                        factor_value: params.data.measure_factor_value,
                      } as IMeasure,
                      {
                        numberFormatService: formService.numberFormatService,
                        measureTransactionType: MEASURE_TRANSACTION.Transfer,
                        notNeedAddSign: true,
                      },
                    )
                  : params.data[formService.formFieldPath.measureEntityId];
              },
              cellClassRules: {
                'cell-overflow-visible-always': (): boolean =>
                  !formService.getIsDisableTopSection(),
              },
              cellClass: (): string | string[] => {
                return formService.isFinalStatusFun()
                  ? ''
                  : 'cell-price ag-cell-editable-renderer cell-overflow-visible-without-hover';
              },
              minWidth: 120,
            },
            getParQtyColumn(formService),
            {
              path: formFieldPath.initialCount,
              title: I18n.Forms.TransferForm.QtyRequested,
              defaultView: true,
              editable: true,
              valueFormatter: (params: ValueFormatterParams): string =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(false)(
                      params,
                    )
                  : null,
              cellRendererSelector: (
                params: ICellRendererParams,
              ): ComponentSelectorResult | null => {
                return params.data.isLeaf && !formService.isFinalStatusFun()
                  ? { component: CELL_RENDERER.float_input_renderer }
                  : null;
              },
              cellClassRules: formService.getQuantityCellClassRules(
                TRANSFER_NOT_FINAL_STATUS,
              ),
              cellRendererParams: (
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                params: ICellRendererParams,
              ): ListColumn['cellRendererParams'] => {
                return {
                  defaultValue: null,
                  placeholder: '0.00',
                  disabled: !editor.canEdit,
                  disableKeyUpFinishEditing: true,
                  min: MIN_NUMBER_FOR_TRANSFER_QUANTITY,
                  max: MAX_NUMBER_FOR_TRANSFER_QUANTITY,
                  acceptableInputRegex: generateDecimalRegexp(
                    formService.stateService.getNumberPrecision(),
                    false,
                  ),
                  usePreferenceDecimalPrecision: {
                    overrideMax: true,
                    overrideMin: true,
                  },
                  artificialInputCheck: true,
                };
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: 'item_cost',
              title: I18n.Forms.GridForm.UnitCost,
              defaultView: true,
              headerComponentParams: {
                hintOptions: {
                  description: editor.i18nService.translate(
                    I18n.InfoMessages.TransferUnitCostTooltips,
                  ),
                  placement: 'top',
                },
              },
              valueFormatter: (params: ValueFormatterParams): string | null =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(true)(
                      params,
                    )
                  : null,
              valueGetter: (params: ValueGetterParams): number | null => {
                if (!params.data.isLeaf) return null;

                return formService.isFinalStatusFun()
                  ? params.data.item_cost
                  : calculateUnitCost(params);
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: formFieldPath.count,
              title: I18n.Forms.TransferForm.QtyReceived,
              defaultView: true,
              editable: true,
              hide: true,
              valueFormatter: (params: ValueFormatterParams): string =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(false)(
                      params,
                    )
                  : null,
              cellRendererSelector: (
                params: ICellRendererParams,
              ): ComponentSelectorResult | null => {
                return params.data.isLeaf && !formService.isFinalStatusFun()
                  ? { component: CELL_RENDERER.float_input_renderer }
                  : null;
              },
              cellClassRules: formService.getQuantityCellClassRules(
                TRANSFER_NOT_FINAL_STATUS,
              ),
              cellRendererParams: (
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                params: ICellRendererParams,
              ): ListColumn['cellRendererParams'] => {
                return {
                  defaultValue: null,
                  placeholder: '0.00',
                  disabled: !editor.canEdit,
                  usePreferenceDecimalPrecision: {
                    overrideMax: true,
                    overrideMin: true,
                  },
                  disableKeyUpFinishEditing: true,
                  min: MIN_NUMBER_FOR_TRANSFER_QUANTITY,
                  max: MAX_NUMBER_FOR_TRANSFER_QUANTITY,
                  acceptableInputRegex: generateDecimalRegexp(
                    formService.stateService.getNumberPrecision(),
                    false,
                  ),
                  artificialInputCheck: true,
                };
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
            {
              path: 'total_cost',
              title: I18n.Forms.TransferForm.RequestCost,
              defaultView: true,
              valueFormatter: (params: ValueFormatterParams): string =>
                params.data.isLeaf
                  ? formService.numberFormatService.getNumberFormatter(true)(
                      params,
                    )
                  : null,
              valueGetter: (params: ValueGetterParams): DecimalNumber => {
                if (!params.data.isLeaf) return null;
                const isFinalStatus = formService.isFinalStatusFun();
                const unitCost = isFinalStatus
                  ? params.data.item_cost
                  : calculateUnitCost(params);
                const calcCount = formService.getIsShownQuantityReceivedCols()
                  ? params.data.count
                  : params.data.initial_count;

                const totalCost = new DecimalNumber(calcCount).mul(unitCost);

                if (!isFinalStatus) {
                  return totalCost.isNaN ? null : totalCost;
                }

                return new DecimalNumber(params.data.total_cost);
              },
              ...COLUMN_ALIGN_RIGHT,
              minWidth: 120,
            },
          ],
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]:
              formService.getGridReadyHandler(editor),
            [XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSED]: (
              action: XuxGridAction,
            ): void => action.payload?.gridApi.redrawRows(),
            [XUX_GRID_ACTIONS.LIST_GROUP_EXPANDED]: (
              action: XuxGridAction,
            ): void => action.payload?.gridApi.redrawRows(),
          },
        },
        suppressNoPermissionsDisable: true,
        validators: [formService.getInventoryItemVenuesRequireValidator()],
      },
      total_cost: {
        show: false,
        required: false,
      },
      ...totalFields,
    },
    groups: [...panelGroups],
  };
};
